/**
 * Sadržaj pomoći po stranama (dugme „?“ u administrativnom delu).
 * Ključ je putanja strane; za strane sa parametrima (/:id) koristi se
 * najduži prefiks. `related` vodi na povezane strane aplikacije.
 */
export const HELP_CONTENT = {
  "/admin/struktura-preduzeca": {
    title: "Struktura preduzeća",
    sections: [
      {
        text: "Ovde se definiše organizaciona šema preduzeća: Uprava, organizacione jedinice i podjedinice do šest nivoa dubine, kao i radna mesta u svakoj jedinici. Struktura je osnova za sve ostale module: zaposleni, procesi i prava pristupa vezuju se za organizacione jedinice.",
      },
      {
        heading: "Kako se koristi",
        items: [
          "Kliknite na „+“ pored jedinice da dodate podređenu jedinicu ili radno mesto.",
          "Ispod jedinice trećeg nivoa pojavljuje se „+ Dodajte podjedinicu“: podjedinica je jedinica četvrtog, petog ili šestog nivoa i može imati svoja radna mesta i svoje podjedinice.",
          "Dugme „+ Dodajte podjedinicu“ ne postoji na jedinicama šestog nivoa: dubina je ograničena na šest nivoa ispod Uprave.",
          "Klikom na naziv jedinice ili radnog mesta možete da ga preimenujete.",
          "Brisanje jedinice briše i sve što je ispod nje: podjedinice, radna mesta i povezane podatke. Pre brisanja sistem traži potvrdu.",
          "Strukturu može da menja samo Administrator.",
          "Celu strukturu (jedinice, radna mesta, zaposlene) možete uneti i odjednom uvozom iz Excela, opisano u odeljku „Uvoz iz Excela“.",
        ],
      },
      {
        heading: "Polja na ovoj strani",
        items: [
          "UPRAVA: vrh strukture; nosi naziv organizacije unet pri registraciji, koji ulazi i u Godišnji izveštaj. Na ovoj strani se ne preimenuje.",
          "ORGANIZACIONA JEDINICA / PODJEDINICA: nivoi organizacije ispod Uprave; svaka jedinica može imati podređene jedinice i radna mesta.",
          "RADNO MESTO: naziv i opis posla; na radna mesta se kasnije raspoređuju zaposleni.",
        ],
      },
      {
        heading: "Uvoz iz Excela",
        items: [
          "Dugmad „UVEZI IZ EXCELA“ i „PREUZMITE ŠABLON“ vide samo Administratori.",
          "„PREUZMITE ŠABLON“ preuzima prazan Excel obrazac sa uputstvom i listama (nivoi, DA/NE, role); popunjava se u njemu, ne u proizvoljnom fajlu.",
          "„UVEZI IZ EXCELA“ radi u koracima: izaberete popunjen .xlsx fajl (najviše 5 MB), kliknete „Proveri“, pregledate šta će se uvesti, pa tek onda „Uvezi“ upisuje. Do klika na „Uvezi“ ništa se ne menja.",
          "Provera izlistava svaki red fajla sa statusom: NOVO (biće uvezeno), VEĆ POSTOJI (preskače se, jer isto već postoji u bazi), UPOZORENJE (npr. osoba na više radnih mesta ili više zaposlenih nego što kaže broj izvršilaca), KONFLIKT (nalog već postoji; preskače se), GREŠKA (red se ne može uvesti, npr. nepoznat nivo ili nadređena jedinica) i INFO (ponovljeni red istog zaposlenog se spaja).",
          "Dok fajl ima ijedan red sa GREŠKA, uvoz nije moguć: ispravite fajl i proverite ga ponovo. Ako ima UPOZORENJA, uvoz traži da potvrdite da ste ih pregledali.",
          "Uvoz dodaje novo i preskače postojeće; ništa ne briše i ne menja iz baze. Zato se isti fajl može uvesti više puta bez opasnosti.",
          "„Izveštaj provere (XLSX)“ preuzima tabelu provere sa statusima i zbirom; korisno za arhivu ili proveru sa kolegama pre uvoza.",
          "Korisnički nalozi (kolona „Korisnički nalog = DA“ u fajlu) se ne otvaraju ovde nego na strani Autorizacija, drugim klikom; vidi pomoć strane Autorizacija.",
        ],
      },
    ],
    related: [
      { label: "Lista zaposlenih", to: "/admin/lista-zaposlenih/0/0" },
      { label: "Autorizacija (nalozi i role)", to: "/admin/autorizacija" },
    ],
  },

  "/admin/lista-zaposlenih": {
    title: "Lista zaposlenih",
    sections: [
      {
        text: "Pregled svih zaposlenih po organizacionim jedinicama, sa radnim mestom i opisom posla.",
      },
      {
        heading: "Kako se koristi",
        items: [
          "„Dodajte zaposlenog“ unosi novog zaposlenog na izabrano radno mesto; o tome nastaje obaveštenje.",
          "Ikonicom olovke menjate podatke zaposlenog, korpom ga brišete ili arhivirate.",
          "Pretraga radi po imenu ili po organizacionoj jedinici.",
          "Administrator menja sve jedinice; Rukovodilac samo svoju organizacionu jedinicu.",
        ],
      },
      {
        heading: "Polja na ovoj strani",
        items: [
          "IME i PREZIME: podaci zaposlenog.",
          "RADNO MESTO: bira se iz radnih mesta definisanih u Strukturi preduzeća; određuje kojoj jedinici zaposleni pripada.",
          "OPIS POSLA: šta zaposleni radi; vezan je za radno mesto i vidi se klikom na zaposlenog.",
        ],
      },
    ],
    related: [
      { label: "Struktura preduzeća", to: "/admin/struktura-preduzeca" },
      { label: "Autorizacija (nalozi i role)", to: "/admin/autorizacija" },
      { label: "Arhiva zaposlenih", to: "/admin/archive-zaposleni" },
    ],
  },

  "/admin/autorizacija": {
    title: "Autorizacija (nalozi i role)",
    sections: [
      {
        text: "Ovde Administrator otvara naloge za prijavu i dodeljuje role. Svaki nalog se vezuje za zaposlenog iz Liste zaposlenih.",
      },
      {
        heading: "Role i njihova prava",
        items: [
          "Admin: puna prava nad celim preduzećem, uključujući strukturu i naloge.",
          "Rukovodilac: vidi i čita sve, a menja isključivo svoju organizacionu jedinicu (npr. rukovodilac sektora finansija menja samo procese, rizike i zaposlene svog sektora). Jedinica mu se dodeljuje pri otvaranju naloga i može biti na bilo kom nivou strukture, uključujući podjedinice četvrtog do šestog nivoa.",
          "Zaposleni: može da pregleda sve module, bez prava izmene.",
        ],
      },
      {
        heading: "Kako se koristi",
        items: [
          "„+ Dodajte korisnika“: izaberite organizacionu jedinicu, zaposlenog, rolu, pa unesite imejl i lozinku; to su podaci za prijavu tog korisnika.",
          "„UVEZI KORISNIKE IZ EXCELA“ otvara više naloga odjednom iz istog fajla kojim se uvozi struktura; vidi odeljak „Uvoz korisničkih naloga iz Excela“.",
          "„IZVEZI SVE PODATKE (JSON)“ preuzima kompletan izvoz podataka vašeg preduzeća.",
          "Otvaranje naloga se beleži u Obaveštenjima i Evidenciji aktivnosti.",
        ],
      },
      {
        heading: "Polja na ovoj strani",
        items: [
          "ORGANIZACIONA JEDINICA i ZAPOSLENI: kome otvarate nalog; zaposleni mora prethodno postojati u Listi zaposlenih.",
          "ROLA: Admin, Rukovodilac ili Zaposleni; određuje šta korisnik sme da menja.",
          "IMEJL ADRESA: korisničko ime za prijavu; mora biti jedinstvena.",
          "LOZINKA: najmanje 6 znakova; korisnik je kasnije sam menja na strani Promena lozinke. Lozinke otvorene uvozom postavlja sistem (privremene lozinke) i niko ih ne vidi osim Administratora koji ih jednom preuzme.",
        ],
      },
      {
        heading: "Uvoz korisničkih naloga iz Excela",
        items: [
          "Dugme „UVEZI KORISNIKE IZ EXCELA“ vide samo Administratori. Koristi isti .xlsx fajl kao uvoz strukture: nalog se otvara za zaposlenog čija kolona „Korisnički nalog“ glasi DA, sa imejlom i rolom iz fajla.",
          "Struktura i zaposleni moraju biti ranije uvezeni (Struktura preduzeća), jer se svaki nalog vezuje za zaposlenog i njegovu organizacionu jedinicu.",
          "Kao i kod strukture: „Proveri“ pokazuje šta će se otvoriti, pa tek „Uvezi“ otvara naloge. Nalozi koji već postoje dobijaju status KONFLIKT i preskaču se, GREŠKA (npr. neispravan imejl) blokira uvoz dok se ne ispravi.",
          "Sistem sam generiše privremenu lozinku za svaki nalog. Lozinke se prikazuju samo jednom, odmah posle uvoza: preuzmite ih dugmetom „Preuzmi izveštaj sa lozinkama (XLSX)“ ili kopirajte, pošto se nigde u aplikaciji ne čuvaju i ne mogu se kasnije ponovo videti.",
          "Ako se privremena lozinka izgubi, Administrator je može zameniti novom ručnom izmenom naloga (polje LOZINKA) na ovoj strani.",
          "Otvaranje naloga uvozom beleži se u Obaveštenjima i Evidenciji aktivnosti (akcija „Uvoz“).",
        ],
      },
    ],
    related: [
      { label: "Lista zaposlenih", to: "/admin/lista-zaposlenih/0/0" },
      { label: "Struktura preduzeća", to: "/admin/struktura-preduzeca" },
    ],
  },

  "/admin/fuk": {
    title: "FUK (finansijsko upravljanje i kontrola)",
    sections: [
      {
        text: "Modul FUK obuhvata popis poslovnih procesa, mape procesa sa procedurama, identifikaciju i procenu rizika, registre i praćenje mera. Preporučeni redosled rada je odozgo nadole:",
      },
      {
        items: [
          "1. Liste poslovnih procesa: popišite procese po organizacionim jedinicama.",
          "2. Mape procesa: za svaki proces opišite tok, aktivnosti i procedure.",
          "3. Formulari rizika: identifikujte i ocenite rizike procesa.",
          "4. Registri i Akcioni planovi: pratite rizike, kontrole i rokove mera.",
        ],
      },
      {
        heading: "Pretraga",
        items: [
          "Polje PRETRAGA u meniju traži po nazivima procedura, mapa procesa, poslovnih procesa i formulara rizika, a od sada i po uputstvima strana: kada pogodi tekst iz pomoći, ponudiće tu stranu kao rezultat (grupa „Stranice i uputstva“).",
          "Pretraga ne razlikuje dijakritiku: „sifra“ pronalazi i „šifra“.",
        ],
      },
    ],
    related: [
      { label: "Liste poslovnih procesa", to: "/admin/poslovni-procesi" },
      { label: "Mape procesa", to: "/admin/mape-procesa" },
      { label: "Formulari rizika", to: "/admin/formulari-rizika" },
      { label: "Registri rizika", to: "/admin/registri-rizika" },
    ],
  },

  "/admin/poslovni-procesi": {
    title: "Liste poslovnih procesa",
    sections: [
      {
        text: "Popis poslovnih procesa po organizacionim jedinicama. Za svaku jedinicu se vodi lista procesa sa šifrom, nazivom i vlasnicima procesa.",
      },
      {
        heading: "Kako se koristi",
        items: [
          "U padajućoj listi izaberite organizacionu jedinicu.",
          "„Dodajte poslovni proces“ otvara popis za izabranu jedinicu; zatim dodajete stavke liste (šifra + naziv) i vlasnike procesa.",
          "Ikonicom olovke menjate listu, korpom je brišete (uz potvrdu); štampač štampa popis.",
          "UVOZ IZ EXCELA (samo Administrator): dugmad u zaglavlju strane. „PREUZMITE ŠABLON“ daje prazan Excel sa sheet-ovima Poslovni procesi, Mape procesa, Aktivnosti procesa, Procedure, Koraci procedure i Rizici; „PREUZMITE PRIMER“ daje isti šablon popunjen primerom (Služba računovodstva) koji možete izmeniti. „UVEZI IZ EXCELA“ prvo pokaže pregled po redovima (NOVO, VEĆ POSTOJI, UPOZORENJE, KONFLIKT, GREŠKA), a upis se izvršava tek kad nema grešaka.",
          "Pravila uvoza: organizaciona jedinica, radna mesta i zaposleni moraju već postojati (vlasnici procesa, potpisnici i odgovorna radna mesta se uparuju po nazivu; nepoznati ostaju prazni uz upozorenje). Proces se prepoznaje po šifri u jedinici i ne menja se ako postoji; proces koji već ima aktivnu mapu zadržava postojeću mapu (mapa iz fajla se preskače). Koraci dijagrama toka koriste simbole Početak, Aktivnost, Kontrola, Odluka i Kraj. Uvoz je sve-ili-ništa: ili se upiše ceo fajl ili ništa.",
          "Izmene može da radi Admin (sve jedinice) i Rukovodilac (svoja jedinica).",
          "Svaka izmena liste pravi obaveštenje vidljivo svim korisnicima preduzeća.",
        ],
      },
      {
        heading: "Polja na ovoj strani",
        items: [
          "ORGANIZACIONA JEDINICA: jedinica čiji popis procesa gledate; bira se u padajućoj listi.",
          "RUKOVODILAC: rukovodilac izabrane jedinice, bira se iz spiska zaposlenih.",
          "ŠIFRA i NAZIV: oznaka i naziv svakog poslovnog procesa u listi (npr. 1.1 Obračun zarada).",
          "VLASNIK PROCESA: jedan ili više zaposlenih odgovornih za proces; biraju se iz spiska.",
        ],
      },
      {
        heading: "Dobro je znati",
        items: [
          "Svaka stavka liste može da dobije svoju mapu procesa i formulare rizika.",
          "Tabela PRIPREMA / KONTROLIŠE / ODOBRAVA na dnu strane odnosi se na listu izabrane organizacione jedinice: „Izmenite tabelu potpisa“ otvara izbor osoba i datuma, a izmene se čuvaju tek klikom na „Sačuvajte tabelu potpisa“; vrednosti ulaze u štampu liste.",
        ],
      },
    ],
    related: [
      { label: "Mape procesa", to: "/admin/mape-procesa" },
      { label: "Formulari rizika", to: "/admin/formulari-rizika" },
      { label: "Struktura preduzeća", to: "/admin/struktura-preduzeca" },
    ],
  },

  "/admin/mape-procesa": {
    title: "Mape procesa",
    sections: [
      {
        text: "Mapa procesa detaljno opisuje jedan poslovni proces: cilj, tok (ulaz, aktivnosti, izlaz), potencijalne rizike, resurse i veze sa drugim procesima, kao i procedure sa pratećom dokumentacijom.",
      },
      {
        heading: "Kako se koristi",
        items: [
          "Plusom kreirate novu mapu i vezujete je za proces iz Liste poslovnih procesa.",
          "Ikonicom olovke ulazite u režim izmene; izmene se čuvaju TEK klikom na „Sačuvajte mapu procesa“; napuštanje strane bez snimanja ih odbacuje. Mapu možete sačuvati i pre nego što popunite sva obavezna polja (CILJ PROCESA, ULAZ, AKTIVNOSTI, IZLAZ) — nepopunjena polja ostaju označena, a pri prelasku na drugu stranu kroz meni sa nesačuvanim izmenama aplikacija traži potvrdu.",
          "U tabeli „ŠIFRE I NAZIV PROCEDURA“ olovka otvara dokumentaciju procedure (odgovornosti, termini, zakonska regulativa, aktivnosti).",
          "U redu PRIPREMA / KONTROLIŠE / ODOBRAVA birate odgovorne osobe i datume.",
          "Svako snimanje automatski čuva prethodno stanje kao arhiviranu verziju; dugme „ISTORIJA“ prikazuje sve ranije verzije (sa oznakom verzije, korisnikom i datumom „važila do“) i svaku možete odštampati. Isto važi i za procedure (dugme ISTORIJA u dokumentaciji procedure).",
          "„ARHIVIRAJ“ ručno sklanja celu mapu u Arhivu (za procese koji više ne važe); posle arhiviranja poslovni proces se oslobađa, pa za njega možete napraviti novu mapu. Promena verzije NE arhivira mapu, ona samo dobija novu verziju uz sačuvanu istoriju.",
          "Izmena mape (a posebno promena verzije) pravi obaveštenje vidljivo svim korisnicima preduzeća.",
          "Štampač štampa mapu, a dostupan je i DOCX izvoz. Kada je otvorena dokumentacija procedure, štampač uz mapu štampa i kompletnu dokumentaciju te procedure (podaci procedure, odgovornosti, potpisi i svi koraci sa rokovima).",
        ],
      },
      {
        heading: "Polja na ovoj strani",
        items: [
          "PUN NAZIV ORGANIZACIJE, ORGANIZACIONA JEDINICA i NAZIV PROCESA: kome mapa pripada; naziv procesa se bira iz Liste poslovnih procesa.",
          "ŠIFRA PROCESA i RUKOVODILAC ORGANIZACIONE JEDINICE: izvedena polja, popunjavaju se sama na osnovu izabranog procesa.",
          "ŠIFRA ORGANIZACIONE JEDINICE: neobavezan ručni unos (npr. šifra koju organizacija već koristi); aplikacija je ne generiše.",
          "VLASNIK PROCESA: vlasnici definisani na listi poslovnih procesa.",
          "CILJ PROCESA, MOGUĆI RIZICI, VEZE SA DRUGIM PROCESIMA, RESURSI: opisna polja mape.",
          "KRATAK OPIS PROCESA (ULAZ, AKTIVNOSTI, IZLAZ): šta u proces ulazi, šta se radi i šta iz njega izlazi.",
          "AKTIVNOSTI U PROCESU: tabela aktivnosti sa opisima, korak po korak.",
          "ŠIFRE I NAZIV PROCEDURA: procedure mape; olovka otvara dokumentaciju procedure (odgovornosti i ovlašćenja, termini i skraćenice, zakonska regulativa, aktivnosti sa rokovima i odgovornima).",
          "DIJAGRAM TOKA: simboli „Početak“ i „Kraj“ su grafički elementi i ne zahtevaju naziv ni opis; za aktivnost, kontrolu i odluku naziv i opis su obavezni.",
          "PROPRATNI DOKUMENTI: u režimu izmene reda možete upisati samo naziv dokumenta (npr. dokument koji postoji samo u papiru), otpremiti fajl sa računara gornjom ikonicom, dodati link donjom ikonicom, ili bilo koju kombinaciju; ništa nije obavezno, a „x“ uklanja zapis. Dozvoljeni fajlovi su PDF, DOC, DOCX, XLS, XLSX, JPG i PNG, najviše 25 MB po fajlu. Otpremljeni fajl se vezuje za red tek kada snimite red; klik na naziv fajla preuzima ga, a preuzimanje je dostupno svim korisnicima preduzeća. Otpremanje, zamena (x pa nov fajl) i uklanjanje prate ista prava kao izmena reda (Administrator sve mape, Rukovodilac mape svoje jedinice). Klikabilni su samo linkovi koji počinju sa http://, https:// ili www.",
          "PRIPREMA / KONTROLIŠE / ODOBRAVA sa DATUMIMA: ko je izradio, kontrolisao i odobrio dokument i kada.",
          "VERZIJA: oznaka verzije dokumenta (npr. 1.0); pri promeni se prethodno stanje čuva u istoriji.",
        ],
      },
    ],
    related: [
      { label: "Liste poslovnih procesa", to: "/admin/poslovni-procesi" },
      { label: "Formulari rizika", to: "/admin/formulari-rizika" },
      { label: "Arhiva mapa procesa", to: "/admin/archive-mape-procesa" },
    ],
  },

  "/admin/formulari-rizika": {
    title: "Formulari rizika",
    sections: [
      {
        text: "Formular rizika identifikuje i ocenjuje jedan rizik poslovnog procesa: opis, uzrok, posledicu, grupu rizika i ocenu (verovatnoća × uticaj), kao i meru/kontrolu za upravljanje rizikom.",
      },
      {
        heading: "Kako se koristi",
        items: [
          "Plusom kreirate nov formular i vezujete ga za proces; obavezna polja su označena.",
          "„Sačuvajte formular rizika“ čuva izmene; ispod formulara je istorija procene.",
          "Kreiranje, izmena i brisanje formulara beleže se u Obaveštenjima i Evidenciji aktivnosti.",
        ],
      },
      {
        heading: "Polja na ovoj strani",
        items: [
          "IME PROCESA i BROJ: proces na koji se rizik odnosi i redni broj formulara.",
          "IME, OPIS, UZROK i POSLEDICA RIZIKA: rečima opisujete šta može da krene naopako i zašto.",
          "GRUPA RIZIKA: kategorija (npr. finansijski, operativni) radi grupisanja u registru.",
          "VEROVATNOĆA (1-5) i UTICAJ (1-5): ocene; UKUPNA VREDNOST je njihov ZBIR, a procenat = zbir × 10 (ispod 50% nizak, 50-59% srednji, preko 59% visok nivo rizika).",
          "NAČIN UPRAVLJANJA RIZIKOM i OPIS KONTROLA/MERA: šta preduzimate da rizik smanjite.",
          "PROCENJENI UTICAJ KONTROLA U % (0-100): koliko mere smanjuju rizik; iz toga se računa PREOSTALI RIZIK.",
          "ROK ZA IZVRŠENJE, ODGOVORNA OSOBA i STATUS IZVRŠENJA MERA: ulaze u Akcione planove i Registar kontrola.",
          "INTERVJUISANO LICE, DATUM INTERVJUA i FUNKCIJA/VLASNIK RIZIKA: biraju se iz spiska zaposlenih.",
          "POSLEDNJE IZMENE UNEO i DATUM POSLEDNJE IZMENE: popunjava sistem automatski.",
        ],
      },
    ],
    related: [
      { label: "Registri rizika", to: "/admin/registri-rizika" },
      { label: "Akcioni planovi", to: "/admin/akcioni-planovi" },
      { label: "Mape procesa", to: "/admin/mape-procesa" },
      { label: "Arhiva formulara rizika", to: "/admin/archive-formulari-rizika" },
    ],
  },

  "/admin/registri-rizika": {
    title: "Registri rizika",
    sections: [
      {
        text: "Zbirni, pregledni registar svih identifikovanih rizika preduzeća: proces, rizik, ocena, nivo, mere i preostali rizik. Strana je za pregled; rizici se menjaju na svojim formularima.",
      },
      {
        heading: "Kako se koristi",
        items: [
          "Registar se puni automatski iz Formulara rizika.",
          "Dostupni su XLSX i DOCX izvozi registra.",
        ],
      },
      {
        heading: "Kolone tabele",
        items: [
          "VLASNIK PROCESA, BROJ i IME RIZIKA: identifikacija rizika i procesa.",
          "OPIS RIZIKA i GRUPA RIZIKA.",
          "VEROVATNOĆA (1-5), UTICAJ (1-5) i UKUPNA VREDNOST (zbir × 10 daje procenat i nivo rizika).",
          "NAČIN UPRAVLJANJA, OPIS KONTROLA/MERA i PROCENJENI UTICAJ KONTROLA U %.",
          "PREOSTALI RIZIK: rizik koji ostaje posle primene mera.",
        ],
      },
    ],
    related: [
      { label: "Formulari rizika", to: "/admin/formulari-rizika" },
      { label: "Registar kontrola", to: "/admin/registar-kontrola" },
      { label: "Izveštaji", to: "/admin/izvestaji" },
    ],
  },

  "/admin/registar-kontrola": {
    title: "Registar kontrola",
    sections: [
      {
        text: "Pregled svih mera i kontrola definisanih na formularima rizika: šta je mera, kog rizika se tiče, ko je odgovoran i u kom je statusu.",
      },
      {
        heading: "Kako se koristi",
        items: [
          "Registar se puni automatski iz Formulara rizika (polja mere/kontrole).",
          "Za izmenu mere otvorite odgovarajući formular rizika.",
        ],
      },
      {
        heading: "Kolone tabele",
        items: [
          "IZVOR: odakle kontrola potiče (formular rizika ili procedura).",
          "POSLOVNI PROCES i PROCEDURA: na šta se kontrola odnosi.",
          "OPIS KONTROLE/MERE i POVEZANI RIZIK sa NIVOOM RIZIKA.",
          "ODGOVORNO LICE, ROK i STATUS: ko sprovodi, do kada i dokle se stiglo.",
        ],
      },
    ],
    related: [
      { label: "Formulari rizika", to: "/admin/formulari-rizika" },
      { label: "Akcioni planovi", to: "/admin/akcioni-planovi" },
    ],
  },

  "/admin/akcioni-planovi": {
    title: "Akcioni planovi",
    sections: [
      {
        text: "Praćenje mera i rokova: sve mere i aktivnosti sa rokom na jednom mestu, sa statusom roka (u roku, ističe uskoro, probijen rok, završeno).",
      },
      {
        heading: "Kako se koristi",
        items: [
          "Obojene značke iznad tabele filtriraju prikaz po statusu.",
          "Oznaka „Završeno“ u koloni AKCIJE beleži da je mera izvršena (Admin i Rukovodilac).",
          "Štampač štampa pregled akcionih planova.",
        ],
      },
      {
        heading: "Kolone tabele",
        items: [
          "TIP: „Mera“ (iz formulara rizika) ili „Aktivnost“ (iz procedure sa rokom).",
          "NAZIV i OPIS MERE/KONTROLE: šta treba uraditi.",
          "PROCES: poslovni proces na koji se odnosi.",
          "ODGOVORAN: ko sprovodi meru ili aktivnost.",
          "ROK i STATUS ROKA: datum izvršenja i obojena oznaka (u roku, ističe uskoro, probijen rok, završeno).",
          "STATUS IZVRŠENJA: tekstualni status iz formulara rizika.",
          "AKCIJE: oznaka „Završeno“ za izvršene mere.",
        ],
      },
    ],
    related: [
      { label: "Formulari rizika", to: "/admin/formulari-rizika" },
      { label: "Registar kontrola", to: "/admin/registar-kontrola" },
    ],
  },

  "/admin/samoprocena": {
    title: "Upitnik za samoprocenu",
    sections: [
      {
        text: "Godišnji upitnik za samoprocenu sistema FUK po oblastima (kontrolno okruženje, upravljanje rizicima, kontrolne aktivnosti…). Na svako pitanje odgovara se sa DA / NE / DELIMIČNO.",
      },
      {
        heading: "Kako se koristi",
        items: [
          "„Novi upitnik“ kreira upitnik za izabranu godinu (jedan po godini).",
          "Odgovori se čuvaju dugmetom za snimanje; popunjenost se vidi u pregledu.",
          "Završen upitnik se finalizuje i ulazi u Godišnji izveštaj.",
          "Upitnik popunjavaju Admin i Rukovodilac; Zaposleni ima uvid.",
        ],
      },
      {
        heading: "Polja na ovoj strani",
        items: [
          "GODINA: godina na koju se upitnik odnosi (jedan upitnik po godini).",
          "STATUS: „U izradi“ dok se popunjava, „Završen“ posle finalizacije.",
          "POPUNJENOST: koliko je pitanja odgovoreno, sa trakom napretka.",
          "ZAVRŠEN DANA: datum finalizacije.",
          "AKCIJE: otvaranje upitnika radi popunjavanja ili pregleda.",
        ],
      },
    ],
    related: [
      { label: "Godišnji izveštaj", to: "/admin/godisnji-izvestaj" },
    ],
  },

  "/admin/godisnji-izvestaj": {
    title: "Godišnji izveštaj o sistemu FUK",
    sections: [
      {
        text: "Automatski sastavljen godišnji izveštaj: rezime samoprocene po oblastima, broj procesa, mapa i procedura, pregled rizika po nivoima i grupama, statistika mera i zaključak rukovodstva.",
      },
      {
        heading: "Kako se koristi",
        items: [
          "Izaberite godinu u padajućoj listi; podaci se pune automatski.",
          "„Zaključak i napomene“ unosi rukovodstvo i čuva dugmetom „Sačuvaj zaključak“ (Admin i Rukovodilac); zaključak ulazi u štampanu verziju.",
          "Štampač štampa ceo izveštaj sa mestom za potpis.",
        ],
      },
      {
        heading: "Delovi izveštaja",
        items: [
          "1. Rezime samoprocene po oblastima: broj odgovora DA / NE / DELIMIČNO iz upitnika.",
          "2. Poslovni procesi i procedure: broj popisanih procesa, mapa (i odobrenih) i procedura.",
          "3. Registar rizika: ukupan broj rizika po nivoima i grupama.",
          "4. Mere: broj mera po statusu izvršenja.",
          "5. Zaključak i napomene: slobodan tekst rukovodstva, ulazi u štampanu verziju.",
        ],
      },
    ],
    related: [
      { label: "Upitnik za samoprocenu", to: "/admin/samoprocena" },
      { label: "Izveštaji", to: "/admin/izvestaji" },
    ],
  },

  "/admin/izvestaji": {
    title: "Izveštaji",
    sections: [
      {
        text: "Izveštaji po zahtevu: izvoz registara i pregleda u XLSX i DOCX formatu, za dostavljanje internoj reviziji ili nadležnim institucijama.",
      },
      {
        heading: "Kako se koristi",
        items: [
          "Izaberite izveštaj i format; fajl se odmah preuzima.",
        ],
      },
    ],
    related: [
      { label: "Registri rizika", to: "/admin/registri-rizika" },
      { label: "Godišnji izveštaj", to: "/admin/godisnji-izvestaj" },
    ],
  },

  "/admin/notifications": {
    title: "Obaveštenja",
    sections: [
      {
        text: "Obaveštenja pokazuju šta se dešava u aplikaciji na nivou celog preduzeća: svi korisnici vide ista obaveštenja (poslednjih 100), sa imenom korisnika koji je napravio izmenu.",
      },
      {
        heading: "Kada nastaje obaveštenje",
        items: [
          "Kreiranje, izmena i brisanje liste poslovnih procesa.",
          "Izmena i brisanje mape procesa; posebno se beleži kada mapa ili procedura dobije novu verziju (npr. „podigao verziju na 1.1“).",
          "Kreiranje, izmena i brisanje formulara rizika.",
          "Dodavanje zaposlenog i otvaranje korisničkog naloga, uključujući masovno otvaranje naloga uvozom iz Excela.",
          "Arhiviranje poslovnog procesa, mape ili formulara rizika.",
        ],
      },
      {
        heading: "Kako se koristi",
        items: [
          "„Sortiraj po“ ređa obaveštenja po starosti.",
          "„Opcije“ na obaveštenju nude „Pregledajte“ i „Izmenite“ (vode pravo na stranu na koju se izmena odnosi: mapu, formular, listu...) i „Odštampaj“.",
          "Za detaljniji trag (svaka pojedinačna akcija, filtriranje) služi Evidencija aktivnosti.",
        ],
      },
    ],
    related: [
      { label: "Evidencija aktivnosti", to: "/admin/aktivnosti" },
    ],
  },

  "/admin/aktivnosti": {
    title: "Evidencija aktivnosti",
    sections: [
      {
        text: "Detaljan dnevnik svih izmena u aplikaciji (trag revizije): ko je, kada i šta kreirao, izmenio ili obrisao. Dostupno samo Administratoru.",
      },
      {
        heading: "Kako se koristi",
        items: [
          "Filtrirajte po vrsti akcije (kreiranje/izmena/brisanje/preuzimanje/uvoz) i vrsti zapisa; otpremanje i preuzimanje propratnih dokumenata mape procesa beleži se kao zapis vrste „Propratni dokument“.",
          "Akcija „Uvoz“ beleži uvoz organizacione strukture (jedinice, radna mesta, zaposleni) i uvoz korisničkih naloga iz Excela.",
          "Evidencija se puni automatski i ne može se menjati.",
        ],
      },
    ],
    related: [
      { label: "Obaveštenja", to: "/admin/notifications" },
    ],
  },

  "/admin/archive-poslovni-procesi": {
    title: "Arhiva: Liste poslovnih procesa",
    sections: [
      {
        text: "Arhivirani popisi poslovnih procesa. Arhiviranje sklanja zapis iz radnog prikaza, ali ga trajno čuva radi uvida; svako arhiviranje se beleži u Obaveštenjima i Evidenciji aktivnosti.",
      },
    ],
    related: [
      { label: "Liste poslovnih procesa", to: "/admin/poslovni-procesi" },
      { label: "Arhiva mapa procesa", to: "/admin/archive-mape-procesa" },
    ],
  },

  "/admin/archive-mape-procesa": {
    title: "Arhiva: Mape procesa",
    sections: [
      {
        text: "Arhivirane mape procesa. Mapa se arhivira dugmetom „ARHIVIRAJ“ na samoj mapi; ovde je dostupna za pregled, a poslovni proces se oslobađa i može da dobije novu mapu. Ne mešati sa istorijom verzija: prethodne verzije aktivne mape gledaju se dugmetom ISTORIJA na samoj mapi.",
      },
    ],
    related: [
      { label: "Mape procesa", to: "/admin/mape-procesa" },
      { label: "Arhiva formulara rizika", to: "/admin/archive-formulari-rizika" },
    ],
  },

  "/admin/archive-formulari-rizika": {
    title: "Arhiva: Formulari rizika",
    sections: [
      {
        text: "Arhivirani formulari rizika. Klikom na red otvarate formular radi pregleda; arhiviranje se beleži u Obaveštenjima i Evidenciji aktivnosti.",
      },
    ],
    related: [
      { label: "Formulari rizika", to: "/admin/formulari-rizika" },
      { label: "Arhiva zaposlenih", to: "/admin/archive-zaposleni" },
    ],
  },

  "/admin/archive-zaposleni": {
    title: "Arhiva: Zaposleni",
    sections: [
      {
        text: "Lista arhiviranih (bivših) zaposlenih. Zaposleni se arhivira iz Liste zaposlenih i ovde ostaje zabeležen.",
      },
    ],
    related: [
      { label: "Lista zaposlenih", to: "/admin/lista-zaposlenih/0/0" },
    ],
  },

  "/admin/promena-lozinke": {
    title: "Promena lozinke",
    sections: [
      {
        text: "Promena lozinke naloga kojim ste trenutno prijavljeni.",
      },
      {
        heading: "Kako se koristi",
        items: [
          "Unesite trenutnu lozinku, pa novu lozinku dva puta (najmanje 6 znakova).",
          "Lozinke drugih korisnika menja Administrator na strani Autorizacija.",
        ],
      },
      {
        heading: "Polja na ovoj strani",
        items: [
          "TRENUTNA LOZINKA: postojeća lozinka, radi potvrde identiteta.",
          "NOVA LOZINKA i POTVRDA: nova lozinka uneta dva puta; moraju se poklopiti.",
        ],
      },
    ],
    related: [
      { label: "Autorizacija", to: "/admin/autorizacija" },
    ],
  },

  "/admin/contact": {
    title: "Kontakt",
    sections: [
      {
        text: "Ako imate problem u radu sa aplikacijom ili pitanje, ovde možete poslati poruku podršci.",
      },
    ],
    related: [],
  },
};

/** Podrazumevani sadržaj kada strana nema svoj unos. */
export const HELP_DEFAULT = {
  title: "Pomoć",
  sections: [
    {
      text: "Za ovu stranu još ne postoji posebno uputstvo. Kroz aplikaciju se krećete pomoću menija sa leve strane; dugme „?“ na svakoj strani otvara objašnjenje te strane.",
    },
  ],
  related: [{ label: "FUK početna strana", to: "/admin/fuk" }],
};
